import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

/**
 * "There's nothing here" for card grids and panels.
 *
 * DataTable already owns the empty state *inside* a table (`emptyMessage`).
 * This is the one for everything else — a grid of cards, a panel, a section
 * that hasn't been populated yet.
 *
 * Say what would be here and how it gets here. "No shipments recorded" leaves
 * the reader wondering whether that's a bug; "No shipments yet — recording one
 * puts it here" tells them nothing is broken and what to do. That is what
 * `action` and the child text are for.
 */
export function EmptyState({
  title,
  children,
  icon,
  action,
  /** Drop the Card chrome — for an empty state already inside a bordered box. */
  bare = false,
  className,
}: {
  title: React.ReactNode;
  children?: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
  bare?: boolean;
  className?: string;
}) {
  const body = (
    <div
      className={cn(
        "flex flex-col items-center gap-2 py-8 text-center",
        bare && className,
      )}
    >
      {icon && <div className="text-muted-foreground [&_svg]:h-6 [&_svg]:w-6">{icon}</div>}
      <p className="text-sm font-medium">{title}</p>
      {children && (
        <p className="max-w-md text-sm text-muted-foreground">{children}</p>
      )}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );

  if (bare) return body;
  return (
    <Card className={className}>
      <CardContent className="p-0">{body}</CardContent>
    </Card>
  );
}
